import React, { useState, useEffect } from 'react';
import ragService from '../../services/rag.service';
import { useAuthStore } from '../../store/authStore';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';

const Documents = () => {
  const user = useAuthStore((state) => state.user);
  const canManage = user?.role === 'superadmin';
  const [documents, setDocuments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState('all');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

  useEffect(() => {
    loadCollections();
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (allDocumentsRef.current.length > 0) {
      applyFilter();
    }
  }, [selectedCollectionId]);

  // Keep a ref to all documents for client-side filtering
  const allDocumentsRef = React.useRef([]);

  const loadCollections = async () => {
    try {
      const response = await ragService.getCollections();
      console.log('✅ Collections loaded:', response);
      setCollections(response.collections || []);
    } catch (err) {
      console.error('❌ Failed to load collections', err);
    }
  };

  const applyFilter = () => {
    const all = allDocumentsRef.current;
    if (selectedCollectionId === 'all') {
      setDocuments(all);
    } else {
      const selectedCollection = collections.find(c => String(c.id) === selectedCollectionId);
      const filterName = selectedCollection?.name || selectedCollection?.collection_name || '';
      setDocuments(all.filter(d => d.collectionName === filterName));
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      // Use the DB-backed endpoint that returns both uploads + scraped pages
      const response = await ragService.listAllDocuments();
      console.log('📊 Documents from DB:', response);

      const docs = (response.documents || []).map(doc => ({
        id: doc.id,
        name: doc.source_type === 'web_scrape' ? (doc.source_url || doc.filename) : (doc.original_filename || doc.filename),
        title: doc.page_title || doc.original_filename || '',
        chunks: doc.chunk_count || 0,
        collection: doc.collection_name || 'default',
        collectionName: doc.collection_name || '',
        sourceType: doc.source_type || 'file_upload',
        status: doc.is_processed ? 'processed' : 'pending',
        fileType: doc.file_type,
        fileSize: doc.file_size_bytes,
        createdAt: doc.created_at,
        sourceUrl: doc.source_url,
      }));

      allDocumentsRef.current = docs;

      // Apply collection filter
      if (selectedCollectionId === 'all') {
        setDocuments(docs);
      } else {
        const selectedCollection = collections.find(c => String(c.id) === selectedCollectionId);
        const filterName = selectedCollection?.name || selectedCollection?.collection_name || '';
        setDocuments(docs.filter(d => d.collectionName === filterName));
      }
    } catch (err) {
      console.error('❌ Error fetching documents:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load documents';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDocDialog = (doc) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDocument = async () => {
    const doc = docToDelete;
    if (!doc) return;

    setDeleteDialogOpen(false);
    setDeletingDocId(doc.id);
    setError('');
    setSuccessMessage('');

    try {
      console.log('🗑️ Deleting document:', doc);
      
      // Find the collection ID from the collection name
      const matchedCollection = collections.find(c => 
        (c.name || c.collection_name) === doc.collectionName
      );
      const collectionId = matchedCollection?.id || doc.collectionName;
      
      // Call the delete endpoint with collection ID and document source
      const response = await ragService.deleteDocument(collectionId, doc.name);
      
      console.log('✅ Delete response:', response);
      setSuccessMessage(`Document "${doc.name}" deleted successfully`);
      
      // Refresh documents list
      await fetchDocuments();
    } catch (err) {
      console.error('❌ Error deleting document:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete document';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeleteAllDialogOpen(false);
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await ragService.deleteDocuments(1);
      console.log('✅ Delete response:', response);
      setSuccessMessage(response.message || 'All documents deleted successfully');
      await loadCollections();
      await fetchDocuments();
    } catch (err) {
      console.error('❌ Error deleting documents:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete documents';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.collection.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group documents by collection for display
  const documentsByCollection = filteredDocuments.reduce((acc, doc) => {
    const collName = doc.collection;
    if (!acc[collName]) {
      acc[collName] = [];
    }
    acc[collName].push(doc);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="mt-1 text-sm text-gray-600">View and manage uploaded documents &amp; scraped pages</p>
        </div>
        {canManage && (
          <button
            onClick={() => setDeleteAllDialogOpen(true)}
            disabled={loading || documents.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Delete All
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Collection Filter */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Collection
            </label>
            <select
              value={selectedCollectionId}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Collections</option>
              {collections.map((collection) => (
                <option key={collection.id} value={String(collection.id)}>
                  {collection.name || collection.collection_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Documents
            </label>
            <input
              type="text"
              placeholder="🔍 Search by name or collection..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            All Documents ({filteredDocuments.length})
            {selectedCollectionId !== 'all' && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                in {collections.find(c => String(c.id) === selectedCollectionId)?.name}
              </span>
            )}
          </h2>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'No documents match your search' : 'No documents uploaded yet'}
            </p>
            {!searchTerm && (
              <p className="text-gray-400 text-sm mt-1">Upload documents to get started</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {Object.entries(documentsByCollection).map(([collectionName, docs]) => (
              <div key={collectionName} className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 px-2">
                  📦 {collectionName} ({docs.length} documents)
                </h3>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div key={doc.id} className="px-6 py-3 hover:bg-gray-50 transition-colors rounded">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="text-2xl">{doc.sourceType === 'web_scrape' ? '🌐' : '📄'}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium text-gray-900 truncate">
                                {doc.sourceType === 'web_scrape' && doc.title ? doc.title : doc.name}
                              </h3>
                              {doc.sourceType === 'web_scrape' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  Scraped
                                </span>
                              )}
                            </div>
                            {doc.sourceType === 'web_scrape' && doc.title && (
                              <p className="text-xs text-gray-400 truncate mt-0.5">{doc.name}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                              <span>{doc.chunks} chunks</span>
                              <span>•</span>
                              <span className="flex items-center">
                                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                                  doc.status === 'processed' ? 'bg-green-500' : 'bg-yellow-500'
                                }`}></span>
                                {doc.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        {canManage && (
                          <button
                            onClick={() => openDeleteDocDialog(doc)}
                            disabled={deletingDocId === doc.id}
                            className="ml-4 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                          >
                            {deletingDocId === doc.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Deleting...</span>
                              </>
                            ) : (
                              <>
                                <span>🗑️</span>
                                <span>Delete</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600">Total Sources</p>
            <p className="text-2xl font-bold text-gray-900">{filteredDocuments.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">📄 Uploaded Files</p>
            <p className="text-2xl font-bold text-gray-900">
              {filteredDocuments.filter(d => d.sourceType !== 'web_scrape').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">🌐 Scraped Pages</p>
            <p className="text-2xl font-bold text-blue-700">
              {filteredDocuments.filter(d => d.sourceType === 'web_scrape').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Chunks</p>
            <p className="text-2xl font-bold text-gray-900">
              {filteredDocuments.reduce((sum, doc) => sum + doc.chunks, 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Collections</p>
            <p className="text-2xl font-bold text-gray-900">
              {Object.keys(documentsByCollection).length}
            </p>
          </div>
        </div>
      </div>
      {/* Delete Document Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDocToDelete(null); }}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        entityName={docToDelete?.name || ''}
        warningText={`This will permanently remove the document and all ${docToDelete?.chunks || 0} chunks.`}
        confirmButtonText="Delete Document"
      />

      {/* Delete All Dialog */}
      <ConfirmDeleteDialog
        open={deleteAllDialogOpen}
        onClose={() => setDeleteAllDialogOpen(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Documents"
        entityName="DELETE ALL"
        warningText="This will permanently delete ALL documents and collections. This action cannot be undone."
        confirmButtonText="Delete Everything"
      />
    </div>
  );
};

export default Documents;
